import type { QueryClient } from '@tanstack/react-query';
import type { DirectoryProvider } from '#services/api/provider.ts';
import type {
  InsightsSavedView,
  SavedViewFilters,
  SavedViewsListResponse,
} from '#services/api/saved-views.ts';

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { savedViews } from '#lib/react-query/queries.ts';
import { createSavedView, deleteSavedView, updateSavedView } from '#services/api/saved-views.ts';
import {
  applyFiltersToSearchParams,
  getParsedSearchParams,
  normalizeFilters,
  serializeFilters,
  tabFilterPolicy,
} from '../Home.utils.ts';
import { coordinateAutoViewName, findCoordinateAutoView } from './CoordinateFilters.utils.ts';
import { canonicalizeProviderFilter } from './Provider.utils.ts';

const autoViewUpsertDebounceMs = 1_000;

/**
 * Keeps the Coordinate tab's filter memory — the hidden auto-view — in sync with the filters the
 * user is looking at, so the slice follows them across sessions and devices.
 *
 * Applying the memory is not this hook's job: the auto-view enters the URL as an ordinary
 * `?view=` token (attached on tab entry / document entry) and the loader parses it like any
 * saved view. This hook only decides *when* to write: nothing while current and stored agree, a
 * debounced upsert when they diverge, and an immediate delete when the filters are cleared — a
 * debounced delete would let a quick re-entry re-attach the just-cleared memory as a view token.
 */
export function useCoordinateAutoView(options: {
  parsed: Parameters<typeof serializeFilters>[0]['parsed'];
  providers: readonly DirectoryProvider[] | undefined;
  savedViewsData: SavedViewsListResponse;
}) {
  // Subscribes to persistence-driven cache updates; the loader-provided list seeds the first render.
  const savedViewsQuery = useQuery({ ...savedViews, initialData: options.savedViewsData });
  const persistAutoView = useAutoViewPersistence();

  const currentFilters = normalizeFilters(
    serializeFilters({ parsed: options.parsed, tabType: 'coordinate' })
  );
  const storedFilters = normalizeStoredCoordinateFilters(
    findCoordinateAutoView(savedViewsQuery.data.views)?.filters,
    options.providers
  );
  const currentFiltersKey = JSON.stringify(currentFilters);
  const storedFiltersKey = JSON.stringify(storedFilters);

  React.useEffect(() => {
    if (currentFiltersKey === storedFiltersKey) {
      return;
    }

    if (Object.keys(currentFilters).length === 0) {
      persistAutoView(currentFilters);
      return;
    }

    // Ordinary changes are debounced so chip-by-chip edits coalesce into one write.
    const timer = window.setTimeout(() => {
      persistAutoView(currentFilters);
    }, autoViewUpsertDebounceMs);

    return () => window.clearTimeout(timer);
    // The JSON keys stand in for their objects so the debounce timer only resets on real change.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFiltersKey, storedFiltersKey, persistAutoView]);
}

/**
 * The write side: a mutation that persists one filter state to the auto-view and mirrors the
 * server's answer into the saved-views cache. Failures only log — filter memory is a
 * convenience, and losing one write must never interrupt the tab.
 */
function useAutoViewPersistence(): (filters: SavedViewFilters) => void {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    // The scope serializes runs so a slow create can't race a follow-up change into a duplicate
    // view; each run re-resolves the auto-view from the cache at execution time.
    scope: { id: 'coordinate-auto-view' },
    mutationFn: (filters: SavedViewFilters) => writeAutoView(queryClient, filters),
    onSuccess: (write) => {
      queryClient.setQueryData<SavedViewsListResponse>(savedViews.queryKey, (existing) =>
        withAutoViewWrite(existing, write)
      );
    },
    onError: (error, filters) => {
      // Dimension names only — provider/appointment-type ids are user identifiers.
      console.error('[coordinate-auto-view] failed to persist last-used filters:', {
        dimensions: Object.keys(filters),
        error,
      });
    },
  });

  return mutation.mutate;
}

/** What one persistence run did, so the cache patch mirrors the server exactly. */
type AutoViewWrite =
  | { kind: 'saved'; view: InsightsSavedView }
  | { kind: 'deleted'; viewId: number }
  | { kind: 'noop' };

/**
 * Persists one filter state to the server: update or create the auto-view, or delete it when the
 * filters are empty — "no auto-view" is the tab's spelling of "nothing remembered" (the core API
 * rejects an empty filters object as blank, so a cleared state cannot be stored).
 */
async function writeAutoView(
  queryClient: QueryClient,
  filters: SavedViewFilters
): Promise<AutoViewWrite> {
  const cachedViews =
    queryClient.getQueryData<SavedViewsListResponse>(savedViews.queryKey)?.views ?? [];
  const autoView = findCoordinateAutoView(cachedViews);

  if (Object.keys(filters).length === 0) {
    if (!autoView) {
      return { kind: 'noop' };
    }

    await deleteSavedView(autoView.id);
    return { kind: 'deleted', viewId: autoView.id };
  }

  const response = autoView
    ? await updateSavedView(autoView.id, { filters })
    : await createSavedView({ tabType: 'coordinate', name: coordinateAutoViewName, filters });

  return { kind: 'saved', view: response.view };
}

/** The saved-views list after a persistence run — the cache reducer for {@link AutoViewWrite}. */
function withAutoViewWrite(
  existing: SavedViewsListResponse | undefined,
  write: AutoViewWrite
): SavedViewsListResponse | undefined {
  if (write.kind === 'noop') {
    return existing;
  }

  if (write.kind === 'deleted') {
    return existing
      ? { views: existing.views.filter((view) => view.id !== write.viewId) }
      : existing;
  }

  if (!existing) {
    return { views: [write.view] };
  }

  const isKnownView = existing.views.some((view) => view.id === write.view.id);

  return {
    views: isKnownView
      ? existing.views.map((view) => (view.id === write.view.id ? write.view : view))
      : [...existing.views, write.view],
  };
}

// Round-trips stored view filters through the URL parse/serialize pipeline so stored state is
// scoped to the tab's dimensions and compared through the same code path as live URL state —
// junk keys and stale non-positive ids can never make the comparison drift. The scoping keys
// come from the policy table, so a new Coordinate filter can't diverge this side from
// `serializeFilters` and turn every mount into a spurious upsert.
function normalizeStoredCoordinateFilters(
  filters: SavedViewFilters | undefined,
  providers: readonly DirectoryProvider[] | undefined
): SavedViewFilters {
  const scopedFilters = Object.fromEntries(
    tabFilterPolicy.coordinate.keys
      .filter((key) => filters?.[key] !== undefined)
      .map((key) => [key, filters?.[key]])
  ) as SavedViewFilters;

  const asParams = applyFiltersToSearchParams(
    normalizeFilters(scopedFilters),
    new URLSearchParams()
  );

  const parsed = getParsedSearchParams(asParams);
  const providerIds = providers
    ? canonicalizeProviderFilter({ providerFilter: parsed.providerIds, providers })
    : parsed.providerIds;

  return normalizeFilters(
    serializeFilters({ parsed: { ...parsed, providerIds }, tabType: 'coordinate' })
  );
}
